import React, { useMemo } from "react";
import { ChartLine, ChartBar, ChartPie, Crosshair, MapPin } from "@phosphor-icons/react";

/**
 * Self-contained SVG charts for the Symptom Tracker. No third-party chart
 * library — everything renders with native SVG so PDF screenshotting and
 * server-side rendering both work cleanly.
 *
 *   1. 30-day severity trendline (with mood overlay + severity zones)
 *   2. Top 6 triggers — horizontal bars
 *   3. Life-impact frequency — horizontal bars (work, sleep, mobility, etc.)
 *   4. Daily-vs-flare donut
 *   5. Most-affected body regions (Pro-only data)
 */
export default function SymptomCharts({ items = [], days = 30 }) {
  const data = useMemo(() => prepareData(items, days), [items, days]);

  if (!items.length) return null;

  return (
    <div className="sym-charts-grid" data-testid="symptom-charts">
      <ChartCard title="Severity & mood trend" sub={`Last ${days} days`} icon={ChartLine} testid="chart-trend">
        <SeverityTrend points={data.trend} days={days} />
      </ChartCard>

      <ChartCard title="Top triggers" sub="What's setting things off" icon={Crosshair} testid="chart-triggers">
        <HBars rows={data.triggers} emptyLabel="No triggers logged yet" maxRows={6} accent="var(--gold-500)" />
      </ChartCard>

      <ChartCard title="Life impact" sub="Where it's hitting hardest" icon={ChartBar} testid="chart-impact">
        <HBars rows={data.impact} emptyLabel="No impact areas logged yet" maxRows={7} accent="var(--navy-700)" />
      </ChartCard>

      <ChartCard title="Daily vs flare" sub="Entry mix" icon={ChartPie} testid="chart-mix">
        <EntryMixDonut daily={data.dailyCount} flare={data.flareCount} />
      </ChartCard>

      {data.regions.length > 0 && (
        <ChartCard title="Body regions" sub="Most-affected areas" icon={MapPin} testid="chart-regions" wide>
          <RegionStrip rows={data.regions} />
        </ChartCard>
      )}
    </div>
  );
}

// ============================================================================ CARD WRAPPER
function ChartCard({ title, sub, icon: Icon, testid, children, wide = false }) {
  return (
    <div className={`sym-chart-card ${wide ? "sym-chart-card--wide" : ""}`} data-testid={testid}>
      <div className="sym-chart-card-head">
        <Icon size={14} weight="bold" />
        <div>
          <div className="sym-chart-card-title">{title}</div>
          <div className="sym-chart-card-sub">{sub}</div>
        </div>
      </div>
      <div className="sym-chart-card-body">{children}</div>
    </div>
  );
}

// ============================================================================ SEVERITY TREND
function SeverityTrend({ points, days }) {
  // Layout
  const W = 560, H = 200;
  const PAD = { top: 14, right: 14, bottom: 24, left: 28 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  if (points.length === 0) {
    return <EmptyChart label="No entries in this window yet" />;
  }

  // X: each day bucket (we already bucketed). Y: 0..10.
  const xStep = points.length > 1 ? innerW / (points.length - 1) : innerW / 2;
  const yScale = (v) => PAD.top + innerH - (v / 10) * innerH;

  const sevPath = pointsToPath(points.map((p, i) => [PAD.left + i * xStep, yScale(p.sev ?? -1)]).filter((_, i) => points[i].sev != null));
  const moodPath = pointsToPath(points.map((p, i) => [PAD.left + i * xStep, yScale(p.mood ?? -1)]).filter((_, i) => points[i].mood != null));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={`Severity and mood trend over ${days} days`} className="sym-svg">
      {/* Severity zones */}
      <rect x={PAD.left} y={yScale(10)} width={innerW} height={yScale(7) - yScale(10)} fill="rgba(220, 38, 38, 0.06)" />
      <rect x={PAD.left} y={yScale(7)} width={innerW} height={yScale(4) - yScale(7)} fill="rgba(201, 162, 74, 0.07)" />
      <rect x={PAD.left} y={yScale(4)} width={innerW} height={yScale(0) - yScale(4)} fill="rgba(16, 185, 129, 0.05)" />

      {/* Gridlines */}
      {[0, 2, 4, 6, 8, 10].map((g) => (
        <g key={g}>
          <line x1={PAD.left} x2={W - PAD.right} y1={yScale(g)} y2={yScale(g)} stroke="rgba(14,26,47,0.07)" strokeWidth={1} />
          <text x={PAD.left - 6} y={yScale(g) + 3} textAnchor="end" fontSize={9} fill="var(--text-tertiary)" fontFamily="JetBrains Mono, monospace">{g}</text>
        </g>
      ))}

      {/* Mood line (lighter) */}
      {moodPath && (
        <path d={moodPath} fill="none" stroke="var(--gold-500)" strokeWidth={1.5} strokeDasharray="4 3" opacity={0.7} />
      )}
      {/* Severity line (primary) */}
      {sevPath && (
        <path d={sevPath} fill="none" stroke="var(--navy-700)" strokeWidth={2.25} strokeLinecap="round" strokeLinejoin="round" />
      )}

      {/* Dots for each severity reading */}
      {points.map((p, i) => p.sev != null && (
        <g key={`d-${i}`}>
          <circle cx={PAD.left + i * xStep} cy={yScale(p.sev)} r={3.2} fill={severityFill(p.sev)} stroke="white" strokeWidth={1.4} />
          <title>{`${p.label} · sev ${p.sev}${p.mood != null ? ` / mood ${p.mood}` : ""}${p.flare ? " · FLARE" : ""}`}</title>
        </g>
      ))}

      {/* Flare markers — small vertical ticks on the axis line */}
      {points.map((p, i) => p.flare && (
        <line key={`f-${i}`} x1={PAD.left + i * xStep} x2={PAD.left + i * xStep} y1={H - PAD.bottom - 4} y2={H - PAD.bottom + 4} stroke="var(--error)" strokeWidth={2.5} />
      ))}

      {/* X axis labels — first, middle, last */}
      {points.length >= 1 && (
        <text x={PAD.left} y={H - 6} fontSize={9} fill="var(--text-tertiary)" fontFamily="JetBrains Mono, monospace">{points[0].label}</text>
      )}
      {points.length >= 3 && (
        <text x={PAD.left + (points.length / 2) * xStep} y={H - 6} fontSize={9} fill="var(--text-tertiary)" fontFamily="JetBrains Mono, monospace" textAnchor="middle">
          {points[Math.floor(points.length / 2)].label}
        </text>
      )}
      {points.length >= 2 && (
        <text x={W - PAD.right} y={H - 6} fontSize={9} fill="var(--text-tertiary)" fontFamily="JetBrains Mono, monospace" textAnchor="end">{points[points.length - 1].label}</text>
      )}

      {/* Legend */}
      <g transform={`translate(${PAD.left + innerW - 152}, ${PAD.top - 2})`}>
        <line x1={0} x2={14} y1={4} y2={4} stroke="var(--navy-700)" strokeWidth={2.25} />
        <text x={18} y={7} fontSize={10} fill="var(--text-secondary)">Severity</text>
        <line x1={68} x2={82} y1={4} y2={4} stroke="var(--gold-500)" strokeWidth={1.5} strokeDasharray="4 3" />
        <text x={86} y={7} fontSize={10} fill="var(--text-secondary)">Mood</text>
        <line x1={120} x2={120} y1={0} y2={8} stroke="var(--error)" strokeWidth={2} />
        <text x={126} y={7} fontSize={10} fill="var(--text-secondary)">Flare</text>
      </g>
    </svg>
  );
}

function severityFill(s) {
  if (s >= 7) return "var(--error)";
  if (s >= 4) return "var(--gold-500)";
  return "var(--success)";
}

function pointsToPath(coords) {
  if (coords.length === 0) return "";
  return coords.map((c, i) => `${i === 0 ? "M" : "L"} ${c[0].toFixed(1)} ${c[1].toFixed(1)}`).join(" ");
}

// ============================================================================ HORIZONTAL BARS
function HBars({ rows, emptyLabel, maxRows = 6, accent }) {
  if (rows.length === 0) return <EmptyChart label={emptyLabel} />;
  const display = rows.slice(0, maxRows);
  const max = Math.max(...display.map((r) => r.value)) || 1;
  return (
    <div className="sym-hbars">
      {display.map((r) => {
        const pct = (r.value / max) * 100;
        return (
          <div key={r.label} className="sym-hbar-row" data-testid={`hbar-${r.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <span className="sym-hbar-label">{r.label}</span>
            <div className="sym-hbar-track">
              <div className="sym-hbar-fill" style={{ width: `${pct}%`, background: accent }} />
            </div>
            <span className="sym-hbar-val mono">{r.value}</span>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================ DONUT (DAILY vs FLARE)
function EntryMixDonut({ daily, flare }) {
  const total = daily + flare;
  if (total === 0) return <EmptyChart label="No entries yet" />;
  const SIZE = 160, R = 64, CX = SIZE / 2, CY = SIZE / 2, STROKE = 18;
  const flarePct = flare / total;
  const dailyPct = daily / total;
  const circumference = 2 * Math.PI * R;
  const flareLen = circumference * flarePct;
  const dailyLen = circumference * dailyPct;

  return (
    <div className="sym-donut">
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} role="img" aria-label="Daily versus flare entry mix">
        <circle cx={CX} cy={CY} r={R} fill="none" stroke="var(--surface-2)" strokeWidth={STROKE} />
        {daily > 0 && (
          <circle cx={CX} cy={CY} r={R} fill="none" stroke="var(--navy-700)" strokeWidth={STROKE}
                  strokeDasharray={`${dailyLen} ${circumference - dailyLen}`}
                  strokeDashoffset={0}
                  transform={`rotate(-90 ${CX} ${CY})`} strokeLinecap="butt" />
        )}
        {flare > 0 && (
          <circle cx={CX} cy={CY} r={R} fill="none" stroke="var(--error)" strokeWidth={STROKE}
                  strokeDasharray={`${flareLen} ${circumference - flareLen}`}
                  strokeDashoffset={-dailyLen}
                  transform={`rotate(-90 ${CX} ${CY})`} strokeLinecap="butt" />
        )}
        <text x={CX} y={CY - 2} textAnchor="middle" fontSize={28} fontWeight={800} fill="var(--text-primary)" fontFamily="Chivo, sans-serif">{total}</text>
        <text x={CX} y={CY + 16} textAnchor="middle" fontSize={9} letterSpacing="0.12em" fontWeight={700} fill="var(--text-secondary)">ENTRIES</text>
      </svg>
      <div className="sym-donut-legend">
        <div className="sym-donut-legend-row">
          <span className="sym-donut-swatch" style={{ background: "var(--navy-700)" }} />
          <span>Daily</span>
          <span className="mono">{daily}</span>
        </div>
        <div className="sym-donut-legend-row">
          <span className="sym-donut-swatch" style={{ background: "var(--error)" }} />
          <span>Flare</span>
          <span className="mono">{flare}</span>
        </div>
      </div>
    </div>
  );
}

// ============================================================================ BODY REGIONS
function RegionStrip({ rows }) {
  const max = rows[0]?.value || 1;
  return (
    <div className="sym-region-strip">
      {rows.slice(0, 12).map((r) => {
        const intensity = 0.18 + 0.78 * (r.value / max);
        return (
          <div
            key={r.label}
            className="sym-region-pill"
            style={{ background: `rgba(31, 58, 107, ${intensity.toFixed(2)})` }}
            title={`${r.label}: ${r.value} entries`}
          >
            <span className="sym-region-label">{r.label}</span>
            <span className="sym-region-val mono">{r.value}</span>
          </div>
        );
      })}
    </div>
  );
}

function EmptyChart({ label }) {
  return (
    <div className="sym-chart-empty">{label}</div>
  );
}

// ============================================================================ DATA PREP
function prepareData(items, days) {
  const now = new Date();
  const start = new Date(now); start.setDate(start.getDate() - (days - 1));
  start.setHours(0, 0, 0, 0);

  // Build day buckets — one entry per day, holding avg severity / max mood of the day.
  const buckets = [];
  for (let i = 0; i < days; i += 1) {
    const d = new Date(start); d.setDate(start.getDate() + i);
    buckets.push({ date: d, sevs: [], moods: [], flares: 0 });
  }
  items.forEach((s) => {
    if (!s.date) return;
    const d = new Date(s.date);
    if (Number.isNaN(d.getTime())) return;
    if (d < start || d > now) return;
    const dayIdx = Math.floor((d - start) / 86400000);
    if (dayIdx < 0 || dayIdx >= buckets.length) return;
    const b = buckets[dayIdx];
    if (s.severity != null && s.severity !== "") b.sevs.push(Number(s.severity));
    if (s.mood != null && s.mood !== "") b.moods.push(Number(s.mood));
    if (s.entryType === "flare") b.flares += 1;
  });
  const trend = buckets.map((b) => ({
    label: b.date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    sev: b.sevs.length ? round1(avg(b.sevs)) : null,
    mood: b.moods.length ? round1(avg(b.moods)) : null,
    flare: b.flares > 0,
  }));

  // Trigger frequency
  const triggerCounts = {};
  items.forEach((s) => (s.triggers || []).forEach((t) => { if (t) triggerCounts[t] = (triggerCounts[t] || 0) + 1; }));
  const triggers = Object.entries(triggerCounts)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);

  // Impact frequency
  const impactCounts = {};
  items.forEach((s) => (s.impact || []).forEach((t) => { if (t) impactCounts[t] = (impactCounts[t] || 0) + 1; }));
  const impact = Object.entries(impactCounts)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);

  // Body regions
  const regionCounts = {};
  items.forEach((s) => (s.bodyRegions || []).forEach((t) => { if (t) regionCounts[t] = (regionCounts[t] || 0) + 1; }));
  const regions = Object.entries(regionCounts)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);

  // Daily vs flare
  const dailyCount = items.filter((s) => s.entryType !== "flare").length;
  const flareCount = items.filter((s) => s.entryType === "flare").length;

  return { trend, triggers, impact, regions, dailyCount, flareCount };
}

function avg(arr) { return arr.reduce((a, b) => a + b, 0) / arr.length; }
function round1(n) { return Math.round(n * 10) / 10; }
